/**
 * Review what an AI proposed before anything is created. Imports show the source document on
 * the left (from 1440 px) and the proposed tree on the right; every item is accepted, edited
 * or rejected on its own, and nothing lands until Apply. Edits show before and after; found
 * duplicates show as links to accept.
 *
 * Keyboard: j and k move, a accepts, r rejects, e edits, Ctrl Enter applies.
 */
import { ArrowRight, Check, ChevronDown, ChevronRight, CircleHelp, FileText, Lightbulb, Link2, Sparkles, Trash2, X } from '@orgus/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import {
  ITEM_KINDS,
  ITEM_KIND_LABEL,
  ITEM_PRIORITIES,
  ITEM_PRIORITY_LABEL,
  LINK_LABEL,
  PARENT_KINDS,
  SEVERITIES,
  SEVERITY_LABEL,
  criterionText,
  uncoveredSections,
} from '@orgus/protocol';
import type { AcceptanceInput, BacklogItem, Decision, Id, ItemKind, ItemPriority, Proposal, ProposedEdit, ProposedItem, ProposedLink, Severity } from '@orgus/protocol';
import { Button, Checkbox, Drawer, ErrorState, IconButton, Input, Markdown, Pressable, Select, Textarea, Toggle, Tooltip, cn, formatInt, hasMod, isTypingTarget, plural, toast } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { KindIcon } from '../missions/parts.tsx';
import { applyProposal, discardProposal, patchProposal } from './data.ts';
import { decide, decideAll, decisionCounts, flatReview, itemsForSection, proposalTree } from './model.ts';
import { KeyLabel } from './parts.tsx';

/** Indentation per depth of the proposed tree, and per heading level of the source. */
const INDENT = ['', 'ml-6', 'ml-12', 'ml-16'];
const SECTION_INDENT = ['', 'ml-3', 'ml-6', 'ml-8'];

const KIND_TITLE: Record<Proposal['kind'], string> = {
  import: 'Review the import',
  draft: 'Review the draft',
  split: 'Review the split',
  edit: 'Review the changes',
  links: 'Review the duplicates',
  defect: 'Review the defect',
};

function DecisionButtons({ value, onChange, label }: { value: Decision; onChange: (d: Decision) => void; label: string }) {
  return (
    <span className="flex shrink-0 items-center gap-1" role="group" aria-label={`Decision on ${label}`} onClick={(e) => e.stopPropagation()}>
      <Tooltip content="Accept" shortcut={['a']}>
        <Toggle variant="outline" size="sm" pressed={value === 'accepted'} aria-label={`Accept ${label}`} onPressedChange={(on) => onChange(on ? 'accepted' : 'pending')}>
          <Check />
        </Toggle>
      </Tooltip>
      <Tooltip content="Reject" shortcut={['r']}>
        <Toggle variant="outline" size="sm" pressed={value === 'rejected'} aria-label={`Reject ${label}`} onPressedChange={(on) => onChange(on ? 'rejected' : 'pending')}>
          <X />
        </Toggle>
      </Tooltip>
    </span>
  );
}

/** The editor of one proposed item, opened in place. */
function ItemEditor({ item, onChange, parentKind }: { item: ProposedItem; onChange: (patch: Partial<ProposedItem>) => void; parentKind: ItemKind | null }) {
  const kinds = ITEM_KINDS.filter((k) => !parentKind || PARENT_KINDS[k].includes(parentKind));
  const setAc = (i: number, patch: Partial<AcceptanceInput>) => onChange({ acceptance: item.acceptance.map((c, k) => (k === i ? { ...c, ...patch } : c)) });
  return (
    <div className="flex flex-col gap-3 border-t border-border-subtle bg-bg px-3 py-3" onClick={(e) => e.stopPropagation()}>
      <Input label="Title" size="sm" value={item.title} onChange={(e) => onChange({ title: e.target.value })} maxLength={200} />
      <div className="grid grid-cols-3 gap-2">
        <Select label="Kind" size="sm" value={item.kind} onValueChange={(v) => onChange({ kind: v as ItemKind, defect: v === 'defect' ? item.defect ?? { severity: 'major', environment: '', steps: '', expected: '', actual: '' } : null })} options={kinds.map((k) => ({ value: k, label: ITEM_KIND_LABEL[k] }))} />
        <Select label="Priority" size="sm" value={String(item.priority)} onValueChange={(v) => onChange({ priority: Number(v) as ItemPriority })} options={ITEM_PRIORITIES.map((p) => ({ value: String(p), label: ITEM_PRIORITY_LABEL[p] }))} />
        <Input label="Points" size="sm" inputMode="numeric" value={item.estimate ?? ''} onChange={(e) => onChange({ estimate: e.target.value.trim() === '' ? null : Number(e.target.value.replace(/[^\d]/g, '')) || 0 })} />
      </div>
      <Textarea label="Description" rows={3} value={item.body} onChange={(e) => onChange({ body: e.target.value })} />
      {item.kind === 'defect' && item.defect && (
        <div className="grid grid-cols-2 gap-2">
          <Select label="Severity" size="sm" value={item.defect.severity} onValueChange={(v) => onChange({ defect: { ...item.defect!, severity: v as Severity } })} options={SEVERITIES.map((s) => ({ value: s, label: SEVERITY_LABEL[s] }))} />
          <Input label="Environment" size="sm" value={item.defect.environment} onChange={(e) => onChange({ defect: { ...item.defect!, environment: e.target.value } })} />
          <Textarea label="Steps to reproduce" rows={3} value={item.defect.steps} onChange={(e) => onChange({ defect: { ...item.defect!, steps: e.target.value } })} wrapperClassName="col-span-2" />
          <Textarea label="Expected" rows={2} value={item.defect.expected} onChange={(e) => onChange({ defect: { ...item.defect!, expected: e.target.value } })} />
          <Textarea label="Actual" rows={2} value={item.defect.actual} onChange={(e) => onChange({ defect: { ...item.defect!, actual: e.target.value } })} />
        </div>
      )}
      <div className="flex flex-col gap-2">
        <span className="text-ui font-medium text-fg-muted">Acceptance criteria</span>
        {item.acceptance.map((c, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="flex h-control-sm w-10 shrink-0 items-center text-caption text-fg-subtle num">AC-{i + 1}</span>
            {c.format === 'gwt' ? (
              <div className="grid flex-1 grid-cols-3 gap-2">
                <Input aria-label={`AC-${i + 1} given`} size="sm" placeholder="Given" value={c.given ?? ''} onChange={(e) => setAc(i, { given: e.target.value })} />
                <Input aria-label={`AC-${i + 1} when`} size="sm" placeholder="When" value={c.when ?? ''} onChange={(e) => setAc(i, { when: e.target.value })} />
                <Input aria-label={`AC-${i + 1} then`} size="sm" placeholder="Then" value={c.then ?? ''} onChange={(e) => setAc(i, { then: e.target.value })} />
              </div>
            ) : (
              <Input aria-label={`AC-${i + 1}`} size="sm" value={c.text} onChange={(e) => setAc(i, { text: e.target.value })} wrapperClassName="flex-1" />
            )}
            <IconButton label={`Remove AC-${i + 1}`} icon={<Trash2 />} size="xs" variant="danger" onClick={() => onChange({ acceptance: item.acceptance.filter((_, k) => k !== i) })} />
          </div>
        ))}
        <div>
          <Button variant="ghost" size="sm" onClick={() => onChange({ acceptance: [...item.acceptance, { format: 'gwt', text: '', given: '', when: '', then: '' }] })}>
            Add criterion
          </Button>
        </div>
      </div>
    </div>
  );
}

function Before({ label, before, after, markdown }: { label: string; before: string; after: string; markdown?: boolean }) {
  if (before.trim() === after.trim()) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-caption font-medium text-fg-subtle">{label}</span>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div className="whitespace-pre-line rounded-control border border-border-subtle bg-bg px-3 py-2 text-ui text-fg-muted line-through decoration-fg-faint">{markdown ? <Markdown source={before || '(empty)'} /> : before || '(empty)'}</div>
        <div className="whitespace-pre-line rounded-control border border-accent-line bg-accent-soft px-3 py-2 text-ui text-fg">{markdown ? <Markdown source={after || '(empty)'} /> : after || '(empty)'}</div>
      </div>
    </div>
  );
}

export function ProposalReview({
  proposal,
  items,
  open,
  onOpenChange,
  onCreated,
}: {
  proposal: Proposal | null;
  items: readonly BacklogItem[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (ids: Id[]) => void;
}) {
  const [draft, setDraft] = useState<Proposal | null>(proposal);
  const [active, setActive] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [section, setSection] = useState<string | null>(null);
  const [busy, setBusy] = useState<'apply' | 'discard' | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const dirty = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraft(proposal);
    setActive(proposal?.items[0]?.ref ?? null);
    setEditing(null);
    setSection(null);
    setError(null);
    dirty.current = false;
    // A new proposal opened; live updates of the same one must not undo what the person decided.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal?.id]);

  const tree = useMemo(() => (draft ? proposalTree(draft.items) : []), [draft]);
  const flat = useMemo(() => flatReview(tree), [tree]);
  const counts = draft ? decisionCounts(draft) : { accepted: 0, rejected: 0, pending: 0, total: 0 };
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const uncovered = useMemo(() => (draft?.source ? uncoveredSections(draft.source.sections, draft.items) : []), [draft]);
  const closed = draft?.status !== 'open';

  const setItems = (next: ProposedItem[]) => {
    dirty.current = true;
    setDraft((d) => (d ? { ...d, items: next } : d));
  };
  const setItem = (ref: string, patch: Partial<ProposedItem>) => draft && setItems(draft.items.map((i) => (i.ref === ref ? { ...i, ...patch } : i)));
  const setEdit = (i: number, d: Decision) => {
    dirty.current = true;
    setDraft((p) => (p ? { ...p, edits: p.edits.map((e, k) => (k === i ? { ...e, decision: d } : e)) } : p));
  };
  const setLink = (i: number, d: Decision) => {
    dirty.current = true;
    setDraft((p) => (p ? { ...p, links: p.links.map((l, k) => (k === i ? { ...l, decision: d } : l)) } : p));
  };

  const close = async (o: boolean) => {
    if (!o && draft && dirty.current && draft.status === 'open') {
      // Keep the decisions so far: reopening the review continues from here.
      await patchProposal(draft.id, { items: draft.items, edits: draft.edits, links: draft.links, ...(draft.dropOriginal !== undefined ? { dropOriginal: draft.dropOriginal } : {}), ...(draft.keepQuestions !== undefined ? { keepQuestions: draft.keepQuestions } : {}) }).catch(() => undefined);
    }
    onOpenChange(o);
  };

  const apply = async () => {
    if (!draft || busy) return;
    setBusy('apply');
    setError(null);
    try {
      const r = await applyProposal(draft.id, { items: draft.items, edits: draft.edits, links: draft.links, ...(draft.dropOriginal !== undefined ? { dropOriginal: draft.dropOriginal } : {}), ...(draft.keepQuestions !== undefined ? { keepQuestions: draft.keepQuestions } : {}) });
      const what = [r.created.length && plural(r.created.length, 'item') + ' created', r.updated.length && plural(r.updated.length, 'item') + ' changed', r.linked.length && plural(r.linked.length, 'link') + ' added'].filter(Boolean).join(', ');
      toast.success('Applied', r.skipped.length ? `${what}. ${r.skipped.length} skipped: ${r.skipped[0]}` : what);
      dirty.current = false;
      onCreated(r.created.map((c) => c.id));
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  };

  const discard = async () => {
    if (!draft) return;
    setBusy('discard');
    try {
      await discardProposal(draft.id);
      toast.success('Proposal discarded', draft.title);
      dirty.current = false;
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!draft || closed) return;
    if (e.key === 'Enter' && hasMod(e)) {
      e.preventDefault();
      void apply();
      return;
    }
    if (isTypingTarget(e.target) || e.ctrlKey || e.metaKey || e.altKey) return;
    const i = Math.max(0, flat.findIndex((n) => n.item.ref === active));
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(flat[Math.min(flat.length - 1, i + 1)]?.item.ref ?? null);
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(flat[Math.max(0, i - 1)]?.item.ref ?? null);
    } else if ((e.key === 'a' || e.key === 'r') && active) {
      e.preventDefault();
      const cur = draft.items.find((x) => x.ref === active);
      const want: Decision = e.key === 'a' ? 'accepted' : 'rejected';
      setItems(decide(draft.items, active, cur?.decision === want ? 'pending' : want, e.shiftKey));
    } else if (e.key === 'e' && active) {
      e.preventDefault();
      setEditing((x) => (x === active ? null : active));
    }
  };

  useEffect(() => {
    if (active) listRef.current?.querySelector(`[data-ref="${CSS.escape(active)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!draft) return null;
  const hasSource = !!draft.source?.sections.length;
  const applyCount = counts.accepted;
  const parentKindOf = (x: ProposedItem): ItemKind | null => (x.parentRef ? draft.items.find((p) => p.ref === x.parentRef)?.kind ?? null : x.parentId ? byId.get(x.parentId)?.kind ?? null : null);
  const visible = section ? new Set(itemsForSection(draft.items, section).map((i) => i.ref)) : null;

  const tree$ = (
    <div ref={listRef} className="flex flex-col gap-2" role="list" aria-label="Proposed items">
      {flat.map(({ item: x, depth }) => {
        if (visible && !visible.has(x.ref)) return null;
        const isActive = x.ref === active;
        const open = editing === x.ref;
        return (
          <div
            key={x.ref}
            role="listitem"
            data-ref={x.ref}
            onClick={() => setActive(x.ref)}
            className={cn('overflow-hidden rounded-card border bg-surface transition-colors', INDENT[Math.min(depth, 3)], isActive ? 'border-accent' : 'border-border', x.decision === 'rejected' && 'opacity-60')}
          >
            <div className="flex items-start gap-2 px-3 py-2">
              <DecisionButtons value={x.decision} label={x.title} onChange={(d) => setItems(decide(draft.items, x.ref, d))} />
              <span className="flex h-control-sm items-center">
                <KindIcon kind={x.kind} />
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className={cn('text-ui font-medium text-fg', x.decision === 'rejected' && 'line-through')}>{x.title}</span>
                {x.body && !open && <span className="line-clamp-2 text-ui text-fg-muted">{x.body}</span>}
                {!open && x.acceptance.length > 0 && (
                  <ul className="flex flex-col gap-1">
                    {x.acceptance.slice(0, 4).map((c, i) => (
                      <li key={i} className="flex gap-2 text-caption text-fg-muted">
                        <span className="shrink-0 whitespace-nowrap text-fg-subtle num">AC-{i + 1}</span>
                        <span className="min-w-0">{criterionText({ ...c, text: c.text })}</span>
                      </li>
                    ))}
                    {x.acceptance.length > 4 && <li className="text-caption text-fg-subtle">and {x.acceptance.length - 4} more</li>}
                  </ul>
                )}
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-fg-subtle">
                  <span>{ITEM_KIND_LABEL[x.kind]}</span>
                  <span>{ITEM_PRIORITY_LABEL[x.priority]}</span>
                  {x.estimate != null && <span className="num">{x.estimate} points</span>}
                  {x.defect && <span>{SEVERITY_LABEL[x.defect.severity]}</span>}
                  {x.sections.length > 0 && (
                    <span className="flex items-center gap-1 font-mono">
                      <FileText className="size-3" aria-hidden />
                      {x.sections.join(', ')}
                    </span>
                  )}
                  {x.parentId && byId.get(x.parentId) && (
                    <span>
                      Under <KeyLabel value={byId.get(x.parentId)!.key} />
                    </span>
                  )}
                </span>
                {x.note && (
                  <span className="flex items-start gap-1 text-caption text-fg-subtle">
                    <Lightbulb className="size-3 shrink-0 translate-y-0.5" aria-hidden />
                    {x.note}
                  </span>
                )}
              </div>
              <IconButton
                label={open ? `Close the editor of ${x.title}` : `Edit ${x.title}`}
                icon={open ? <ChevronDown /> : <ChevronRight />}
                size="xs"
                shortcut={['e']}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(open ? null : x.ref);
                }}
              />
            </div>
            {open && <ItemEditor item={x} parentKind={parentKindOf(x)} onChange={(p) => setItem(x.ref, p)} />}
          </div>
        );
      })}
      {visible && visible.size === 0 && <p className="px-1 text-ui text-fg-subtle">Nothing proposed traces to {section}.</p>}
    </div>
  );

  const edits$ = draft.edits.map((e: ProposedEdit, i) => {
    const it = byId.get(e.itemId);
    return (
      <div key={i} className="flex flex-col gap-3 rounded-card border border-border bg-surface px-3 py-3">
        <div className="flex items-center gap-2">
          <DecisionButtons value={e.decision} label={`changes to ${it?.key ?? 'the item'}`} onChange={(d) => setEdit(i, d)} />
          {it && <KindIcon kind={it.kind} />}
          {it && <KeyLabel value={it.key} />}
          <span className="min-w-0 flex-1 truncate text-ui font-medium text-fg">{it?.title ?? 'An item that is no longer here'}</span>
        </div>
        <p className="flex items-start gap-2 text-ui text-fg-muted">
          <Sparkles className="size-4 shrink-0 translate-y-0.5" aria-hidden />
          {e.rationale}
        </p>
        {it && e.patch.title !== undefined && <Before label="Title" before={it.title} after={e.patch.title} />}
        {it && e.patch.body !== undefined && <Before label="Description" before={it.body} after={e.patch.body} markdown />}
        {it && e.patch.estimate !== undefined && <Before label="Estimate" before={it.estimate != null ? `${it.estimate} points` : 'Not estimated'} after={e.patch.estimate != null ? `${e.patch.estimate} points` : 'Not estimated'} />}
        {it && e.patch.acceptance && (
          <Before
            label="Acceptance criteria"
            before={it.acceptance.map((c, k) => `AC-${k + 1} ${c.text}`).join('\n')}
            after={e.patch.acceptance.map((c, k) => `AC-${k + 1} ${criterionText({ ...c, text: c.text })}`).join('\n')}
          />
        )}
        {it && e.patch.defect && <Before label="Steps to reproduce" before={it.defect?.steps ?? ''} after={e.patch.defect.steps ?? ''} />}
      </div>
    );
  });

  const links$ = draft.links.map((l: ProposedLink, i) => {
    const a = byId.get(l.from);
    const b = byId.get(l.to);
    return (
      <div key={i} className="flex items-start gap-2 rounded-card border border-border bg-surface p-3">
        <DecisionButtons value={l.decision} label={`${a?.key} ${LINK_LABEL[l.type].out.toLowerCase()} ${b?.key}`} onChange={(d) => setLink(i, d)} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2 text-ui text-fg">
            <KeyLabel value={a?.key ?? ''} /> {a?.title}
            <span className="flex items-center gap-1 text-fg-subtle">
              <ArrowRight className="size-3" aria-hidden />
              {LINK_LABEL[l.type].out.toLowerCase()}
            </span>
            <KeyLabel value={b?.key ?? ''} /> {b?.title}
          </span>
          <span className="text-caption text-fg-muted">{l.rationale}</span>
        </div>
      </div>
    );
  });

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => void close(o)}
      width={hasSource ? 1200 : 640}
      eyebrow={
        <>
          <Sparkles className="size-3" aria-hidden />
          <span>Drafted by AI</span>
          {draft.source && <span className="truncate">from {draft.source.name}</span>}
        </>
      }
      title={KIND_TITLE[draft.kind]}
      description={closed ? `This proposal was ${draft.status}.` : `Nothing is created or changed until you apply. ${counts.accepted} accepted, ${counts.rejected} rejected, ${counts.pending} to decide.`}
      onKeyDown={onKeyDown}
      footer={
        closed ? (
          <span className="text-ui text-fg-subtle">This proposal was {draft.status}.</span>
        ) : (
          <>
            <Button variant="ghost" icon={<Trash2 />} loading={busy === 'discard'} onClick={() => void discard()} className="mr-auto">
              Discard proposal
            </Button>
            {draft.items.length > 0 && (
              <Button variant="secondary" onClick={() => setItems(decideAll(draft.items, counts.pending || counts.rejected ? 'accepted' : 'pending'))}>
                {counts.accepted === draft.items.length ? 'Undecide all' : 'Accept all'}
              </Button>
            )}
            {draft.edits.length > 0 && counts.accepted === 0 && (
              <Button variant="secondary" onClick={() => setDraft((p) => (p ? { ...p, edits: p.edits.map((e) => ({ ...e, decision: 'accepted' })) } : p))}>
                Accept the changes
              </Button>
            )}
            <Button variant="primary" loading={busy === 'apply'} onClick={() => void apply()} shortcut={['mod', 'enter']} disabled={!applyCount}>
              {draft.kind === 'edit' ? 'Apply changes' : draft.kind === 'links' ? `Link ${plural(applyCount, 'pair')}` : `Create ${plural(draft.items.filter((x) => x.decision === 'accepted').length, 'item')}`}
            </Button>
          </>
        )
      }
    >
      <div className={cn('grid gap-6 px-6 py-4', hasSource && '2xl:grid-cols-12')}>
        {hasSource && (
          <aside className="flex min-w-0 flex-col gap-2 2xl:col-span-5" aria-label="Source document">
            <div className="flex items-center gap-2 text-ui font-medium text-fg-muted">
              <FileText className="size-4" aria-hidden />
              {draft.source!.name}
              <span className="text-caption font-normal text-fg-subtle num">
                {plural(draft.source!.sections.length, 'section')}, {formatInt(draft.source!.chars)} characters
              </span>
            </div>
            {uncovered.length > 0 && (
              <p className="rounded-card border border-status-attention-line bg-status-attention-soft px-3 py-2 text-ui text-fg">
                {plural(uncovered.length, 'section')} {uncovered.length === 1 ? 'has' : 'have'} nothing proposed: {uncovered.map((s) => s.id).join(', ')}. Check whether {uncovered.length === 1 ? 'it states' : 'they state'} a requirement.
              </p>
            )}
            <ol className="flex flex-col gap-1">
              {draft.source!.sections.map((s) => {
                const n = itemsForSection(draft.items, s.id).length;
                const on = section === s.id;
                return (
                  <li key={s.id}>
                    <Pressable
                      onClick={() => setSection(on ? null : s.id)}
                      aria-pressed={on}
                      className={cn('flex flex-col gap-1 rounded-card border px-3 py-2', SECTION_INDENT[Math.min(s.level - 1, 3)], on ? 'border-accent bg-accent-soft' : 'border-border-subtle bg-surface hover:border-border')}
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-caption text-fg-subtle">{s.id}</span>
                        <span className="min-w-0 flex-1 truncate text-ui font-medium text-fg">{s.title}</span>
                        <span className={cn('text-caption num', n ? 'text-fg-subtle' : 'font-medium text-status-attention')}>{n ? plural(n, 'item') : 'Nothing'}</span>
                      </span>
                      {s.text && <span className="line-clamp-3 text-caption text-fg-muted">{s.text}</span>}
                    </Pressable>
                  </li>
                );
              })}
            </ol>
          </aside>
        )}
        <div className={cn('flex min-w-0 flex-col gap-3', hasSource && '2xl:col-span-7')}>
          {section && (
            <p className="flex items-center gap-2 text-ui text-fg-muted">
              Showing what traces to {section}.
              <Button variant="ghost" size="sm" onClick={() => setSection(null)}>
                Show everything
              </Button>
            </p>
          )}
          {draft.items.length > 0 && tree$}
          {draft.edits.length > 0 && <div className="flex flex-col gap-3">{edits$}</div>}
          {draft.links.length > 0 && <div className="flex flex-col gap-2">{links$}</div>}
          {!draft.items.length && !draft.edits.length && !draft.links.length && (
            <p className="flex items-center gap-2 rounded-card border border-border px-3 py-4 text-ui text-fg-muted">
              <Link2 className="size-4" aria-hidden />
              The AI found nothing to propose.
            </p>
          )}
          {draft.kind === 'split' && draft.targetItemId && (
            <Checkbox checked={draft.dropOriginal !== false} onCheckedChange={(v) => setDraft((p) => (p ? { ...p, dropOriginal: v } : p))} label={`Drop ${byId.get(draft.targetItemId)?.key ?? 'the original'} once its parts are created`} hint="The parts link back to it either way." />
          )}
          {(draft.questions.length > 0 || draft.assumptions.length > 0) && (
            <div className="flex flex-col gap-3 rounded-card border border-border bg-surface px-3 py-3">
              {draft.questions.length > 0 && (
                <Block icon={<CircleHelp className="size-4" aria-hidden />} title={`Open questions (${draft.questions.length})`}>
                  <ul className="flex list-disc flex-col gap-1 pl-6 text-ui text-fg">
                    {draft.questions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                  {draft.kind === 'import' && <Checkbox checked={draft.keepQuestions !== false} onCheckedChange={(v) => setDraft((p) => (p ? { ...p, keepQuestions: v } : p))} label="Add them to the first epic created" />}
                </Block>
              )}
              {draft.assumptions.length > 0 && (
                <Block icon={<Lightbulb className="size-4" aria-hidden />} title={`Assumptions (${draft.assumptions.length})`}>
                  <ul className="flex list-disc flex-col gap-1 pl-6 text-ui text-fg-muted">
                    {draft.assumptions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                </Block>
              )}
            </div>
          )}
          {error && <ErrorState variant="inline" error={error} />}
          {!closed && draft.items.length > 1 && <p className="text-caption text-fg-subtle">j and k move, a accepts, r rejects (Shift for everything under it), e edits, Ctrl Enter applies.</p>}
        </div>
      </div>
    </Drawer>
  );
}

function Block({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex items-center gap-2 text-ui font-medium text-fg-muted">
        {icon}
        {title}
      </h3>
      {children}
    </section>
  );
}
